import React, { useCallback, useEffect } from 'react';
import { isFirefox } from 'react-dnd-html5-backend/lib/BrowserDetector';
import { useMedia } from 'react-use';

import { breakpoints } from 'theme';

const useGridScroll = ({
  containerRef,
  stickyCellWidth,
  zoom,
  isMenuOpen,
  onScrollStartReached,
}: {
  containerRef: React.RefObject<HTMLElement>;
  stickyCellWidth: number;
  zoom: number;
  isMenuOpen: boolean;
  onScrollStartReached?: () => void;
}): {
  handleScrollGrid: (position: 'start' | 'end') => void;
  scrollCount: number;
} => {
  const isDownSmPlus = useMedia(breakpoints.downSmPlus);

  const getScrollCount = useCallback((numEvents: number) => {
    const floorEvents = Math.floor(numEvents);
    if (floorEvents <= 8) return 3;
    if (floorEvents <= 10) return 4;
    if (floorEvents <= 13) return 5;
    if (floorEvents <= 16) return 6;
    if (floorEvents <= 19) return 7;
    if (floorEvents <= 22) return 8;
    return 9;
  }, []);

  useEffect(() => {
    localStorage.removeItem('differencesBoxArea');
    localStorage.removeItem('differencesBoxAreaStart');
  }, [zoom, isMenuOpen, isDownSmPlus]);

  const handleScrollGrid = useCallback(
    (positionToScroll: 'start' | 'end') => {
      const storageKey =
        positionToScroll === 'end'
          ? 'differencesBoxArea'
          : 'differencesBoxAreaStart';
      const otherStorageKey =
        positionToScroll === 'end'
          ? 'differencesBoxAreaStart'
          : 'differencesBoxArea';

      const existingDifferencesBoxArea = localStorage.getItem(storageKey);
      const scrollLeft = containerRef.current.scrollLeft;
      const eventBoxWidth = Math.floor(157 * zoom);
      const numberOfEventsInBoxArea =
        (containerRef.current?.getBoundingClientRect()?.width -
          stickyCellWidth * zoom) /
        eventBoxWidth;
      const scrollCount = getScrollCount(numberOfEventsInBoxArea);
      const differencesBoxArea = existingDifferencesBoxArea
        ? 0
        : numberOfEventsInBoxArea - Math.floor(numberOfEventsInBoxArea);

      localStorage.setItem(
        storageKey,
        (Math.round(differencesBoxArea * 100) / 100).toString(),
      );
      localStorage.removeItem(otherStorageKey);

      const scrollOffset =
        (scrollCount - Math.round(differencesBoxArea * 100) / 100) *
        (eventBoxWidth +
          (numberOfEventsInBoxArea - Math.floor(numberOfEventsInBoxArea)) *
            1.25);

      const newScrollLeft =
        positionToScroll === 'end'
          ? scrollLeft + scrollOffset
          : scrollLeft - scrollOffset;

      if (
        scrollLeft === 0 &&
        newScrollLeft <= 0 &&
        positionToScroll === 'start'
      ) {
        if (onScrollStartReached) {
          onScrollStartReached();
        }
        return;
      }

      containerRef.current.scroll({
        top: 0,
        left: newScrollLeft,
        behavior: isFirefox() ? 'auto' : 'smooth',
      });
    },
    [
      containerRef,
      zoom,
      isMenuOpen,
      isDownSmPlus,
      onScrollStartReached,
      getScrollCount,
      stickyCellWidth,
    ],
  );

  const eventBoxWidth = Math.floor(157 * zoom);
  const numberOfEventsInBoxArea = containerRef.current
    ? (containerRef.current.getBoundingClientRect().width -
        stickyCellWidth * zoom) /
      eventBoxWidth
    : 0;

  return {
    handleScrollGrid,
    scrollCount: getScrollCount(numberOfEventsInBoxArea),
  };
};

export default useGridScroll;
